<template>
  <div>
    <v-card>
      <v-card-text>
        <v-text-field
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            label="Schüler, Klasse oder Fach suchen"
            single-line
            hide-details
            clearable/>
      </v-card-text>
      <missing-exam-list
          :exams="current"
          :loading="loading"
          :search="search"
          :saving="saving"
          no-data-text="Keine versäumten Klausuren in Ihren Klausuren"
          v-on="listeners"/>
    </v-card>

    <v-expansion-panels v-if="archived.length" class="mt-4">
      <v-expansion-panel>
        <v-expansion-panel-header>
          <span><v-icon left>mdi-archive-outline</v-icon>Archiv ({{ archived.length }})</span>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <missing-exam-list
              :exams="archived"
              archived
              :search="search"
              :saving="saving"
              no-data-text="Keine archivierten Nachschreiber"
              v-on="listeners"/>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-dialog :value="admission !== null" max-width="480" @input="admission = null">
      <v-card v-if="admission">
        <v-card-title>Für Nachschreibetermin anmelden</v-card-title>
        <v-card-text>
          <p class="mb-2">{{ examLabel(admission) }}</p>
          <p class="mb-0">
            {{ !admission.admitted && admission.absence_type === 'F' ? 'Der Schüler hat die Klausur unentschuldigt versäumt.' : 'Der Schüler hat einen Nachschreibetermin ohne Attest versäumt.' }}
            Mit der Anmeldung wird er trotzdem beim nächsten Nachschreibetermin eingeplant.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn text @click="admission = null">Abbrechen</v-btn>
          <v-btn color="primary" @click="admit">Anmelden</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog :value="separate !== null" max-width="480" @input="separate = null">
      <v-card v-if="separate">
        <v-card-title>Außerplanmäßig nachgeschrieben</v-card-title>
        <v-card-text>
          <p>{{ examLabel(separate) }}</p>
          <v-text-field
              v-model="writtenOn"
              type="date"
              label="Nachgeschrieben am"
              prepend-icon="mdi-calendar"
              :min="dayAfter(separate.date)"
              :max="today"
              :error-messages="writtenOnErrors"
              autofocus/>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn text @click="separate = null">Abbrechen</v-btn>
          <v-btn color="primary" :disabled="!writtenOn" :loading="saving[separate.key]" @click="recordSeparate">
            Speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import {Component, Vue} from "vue-property-decorator";
import {useHttp} from "@/core/http";
import {examKey, MissingExam} from "@/core/models/missing-exam";
import MissingExamList from "@/components/missingExams/MissingExamList.vue";

type Row = MissingExam & { key: string }

@Component({
  components: {MissingExamList},
})
export default class MyMissingExams extends Vue {
  private exams: Array<Row> = []
  private acknowledged: Array<string> = []
  private loading = false
  private saving: Record<string, boolean> = {}
  private search = ''
  private admission: Row | null = null
  private separate: Row | null = null
  private writtenOn = ''
  private writtenOnErrors: Array<string> = []

  private http = useHttp()

  private get today(): string {
    return this.$moment().format('YYYY-MM-DD')
  }

  private get listeners(): Record<string, (exam: Row) => unknown> {
    return {
      'admit': (exam) => this.admission = exam,
      'withdraw-admission': (exam) => this.send(exam, 'destroy', 'admission', 'Anmeldung zurückgenommen'),
      'record-separate': (exam) => this.openSeparate(exam),
      'remove-separate': (exam) => this.send(exam, 'destroy', 'separate-retake', 'Außerplanmäßiges Nachschreiben zurückgenommen'),
      'archive': (exam) => this.archive(exam, true),
      'restore': (exam) => this.archive(exam, false),
    }
  }

  private get current(): Array<Row> {
    return this.exams.filter((exam) => !this.acknowledged.includes(exam.key))
  }

  private get archived(): Array<Row> {
    return this.exams.filter((exam) => this.acknowledged.includes(exam.key))
  }

  private mounted() {
    this.load()
  }

  private load(): Promise<void> {
    this.loading = true

    return this.http.get('missing-exams/mine')
        .then((response: any) => {
          this.exams = response.data.map((exam: MissingExam) => this.rowOf(exam))
          this.acknowledged = response.acknowledged ?? []
        })
        .catch(() => this.notify('error', 'Die Nachschreibeklausuren konnten nicht geladen werden.'))
        .finally(() => {
          this.loading = false
        })
  }

  private examLabel(exam: MissingExam): string {
    return `${exam.student_name} · ${exam.subject_name} vom ${this.$moment(exam.date).format('DD.MM.YYYY')}`
  }

  private dayAfter(date: string): string {
    return this.$moment(date).add(1, 'day').format('YYYY-MM-DD')
  }

  private openSeparate(exam: Row): void {
    this.writtenOn = this.today
    this.writtenOnErrors = []
    this.separate = exam
  }

  private async admit(): Promise<void> {
    const exam = this.admission
    this.admission = null

    if (exam) {
      await this.send(exam, 'put', 'admission', 'Der Schüler ist für den nächsten Nachschreibetermin angemeldet.')
    }
  }

  private async recordSeparate(): Promise<void> {
    const exam = this.separate
    if (!exam) {
      return
    }

    this.writtenOnErrors = []

    if (await this.send(exam, 'put', 'separate-retake', 'Als außerplanmäßig nachgeschrieben gespeichert.', {written_on: this.writtenOn})) {
      this.separate = null
    }
  }

  // Archiving only changes the teacher's own view, so the row moves over without a reload.
  private async archive(exam: Row, archived: boolean): Promise<void> {
    const saved = await this.save(exam, () => archived
        ? this.http.put(this.url(exam, 'acknowledgement'), this.examPayload(exam))
        : this.http.destroy(this.url(exam, 'acknowledgement', true)))

    if (saved) {
      this.acknowledged = archived
          ? [...this.acknowledged, exam.key]
          : this.acknowledged.filter((key) => key !== exam.key)
      this.notify('success', archived ? 'Ins Archiv verschoben.' : 'Archivieren rückgängig gemacht.')
    }
  }

  // The answer carries the exam as planned anew, which replaces the row.
  private send(exam: Row, method: 'put' | 'destroy', action: string, message: string, payload: Record<string, unknown> = {}): Promise<boolean> {
    return this.save(exam, async () => {
      const response: any = method === 'put'
          ? await this.http.put(this.url(exam, action), {...this.examPayload(exam), ...payload})
          : await this.http.destroy(this.url(exam, action, true))

      this.exams.splice(this.exams.indexOf(exam), 1, this.rowOf(response.data))
      this.notify('success', message)
    })
  }

  private async save(exam: Row, request: () => Promise<unknown>): Promise<boolean> {
    this.$set(this.saving, exam.key, true)

    try {
      await request()
      return true
    } catch (error: any) {
      const errors = error?.data?.errors ?? {}
      this.writtenOnErrors = errors.written_on ?? []

      if (!this.writtenOnErrors.length) {
        this.notify('error', (Object.values(errors).flat()[0] as string) ?? error?.data?.message ?? 'Die Änderung konnte nicht gespeichert werden.')
      }

      return false
    } finally {
      this.$set(this.saving, exam.key, false)
    }
  }

  // The exam is named in the query string, as a DELETE request carries no body.
  private url(exam: MissingExam, action: string, withExam = false): string {
    const url = `missing-exams/mine/${exam.student_id}/${action}`

    return withExam ? `${url}?subject_id=${exam.subject_id}&exam_date=${exam.date}` : url
  }

  private examPayload(exam: MissingExam): Record<string, unknown> {
    return {subject_id: exam.subject_id, exam_date: exam.date}
  }

  private rowOf(exam: MissingExam): Row {
    return {...exam, key: examKey(exam.student_id, exam)}
  }

  private notify(type: 'success' | 'error', text: string): void {
    this.$notify({title: type === 'error' ? 'Fehler' : 'Gespeichert', type, text})
  }
}
</script>
